import { z } from 'zod'
import {
	type FormKind,
	SUGGESTED_STANDARD_DOCS,
	SUGGESTED_STUDENT_DOCS,
	catalogFields,
	catalogPrebuiltQuestions,
	floorKeys,
} from './catalog'

export const MIN_LIVING_HISTORY_YEARS = 1
export const MAX_LIVING_HISTORY_YEARS = 10

export function clampMinLivingHistoryYears(
	years: number | null | undefined,
): number {
	if (
		years == null ||
		!Number.isFinite(years) ||
		years < MIN_LIVING_HISTORY_YEARS
	) {
		return MIN_LIVING_HISTORY_YEARS
	}
	return Math.min(MAX_LIVING_HISTORY_YEARS, Math.floor(years))
}

export type CustomDropdownField = {
	id: string
	title: string
	options: string[]
	selection: 'single' | 'multiple'
}

export type CustomQuestion = {
	id: string
	prompt: string
	type: 'yes_no' | 'yes_no_explanation' | 'dropdown'
	options?: string[]
	dropdowns?: CustomDropdownField[]
	enabled?: boolean
	origin: 'custom'
}

export type DocumentItem = {
	id: string
	name: string
	mandatory: boolean
}

export type FieldToggleMap = Record<string, boolean>

export type QuestionBlock = {
	enabled: boolean
	prebuilt: Record<string, boolean>
	custom: CustomQuestion[]
}

export type ToggleSection = {
	enabled: boolean
	fields: FieldToggleMap
}

export type FormConfigV1 = {
	version: 1
	guarantor?: {
		ask: 'every_applicant'
		timing: 'requested' | 'immediate'
	}
	sections: {
		next_of_kin?: ToggleSection
		occupant?: ToggleSection
		pet: ToggleSection
		vehicle: ToggleSection
		min_living_history?: { enabled: boolean; years: number | null }
		landlord_contact: ToggleSection
		rental_questions: QuestionBlock
		employer_contact: ToggleSection
		employment_questions: QuestionBlock
		documents: { enabled: boolean; items: DocumentItem[] }
		terms: {
			enabled: boolean
			fileName?: string | null
			blobUrl?: string | null
		}
	}
}

const CustomDropdownFieldSchema = z.object({
	id: z.string().min(1),
	title: z.string().min(1),
	options: z.array(z.string().min(1)).min(1),
	selection: z.enum(['single', 'multiple']),
})

const CustomQuestionSchema = z.object({
	id: z.string().min(1),
	prompt: z.string().min(1),
	type: z.enum(['yes_no', 'yes_no_explanation', 'dropdown']),
	options: z.array(z.string()).optional(),
	dropdowns: z.array(CustomDropdownFieldSchema).optional(),
	enabled: z.boolean().optional(),
	origin: z.literal('custom'),
})

const DocumentItemSchema = z.object({
	id: z.string().min(1),
	name: z.string().min(1),
	mandatory: z.boolean(),
})

const ToggleSectionSchema = z.object({
	enabled: z.boolean(),
	fields: z.record(z.boolean()),
})

const QuestionBlockSchema = z.object({
	enabled: z.boolean(),
	prebuilt: z.record(z.boolean()),
	custom: z.array(CustomQuestionSchema),
})

export const FormConfigV1Schema = z.object({
	version: z.literal(1),
	guarantor: z
		.object({
			ask: z.literal('every_applicant'),
			timing: z.enum(['requested', 'immediate']),
		})
		.optional(),
	sections: z.object({
		next_of_kin: ToggleSectionSchema.optional(),
		occupant: ToggleSectionSchema.optional(),
		pet: ToggleSectionSchema,
		vehicle: ToggleSectionSchema,
		min_living_history: z
			.object({
				enabled: z.boolean(),
				years: z
					.number()
					.int()
					.min(MIN_LIVING_HISTORY_YEARS)
					.max(MAX_LIVING_HISTORY_YEARS)
					.nullable(),
			})
			.optional(),
		landlord_contact: ToggleSectionSchema,
		rental_questions: QuestionBlockSchema,
		employer_contact: ToggleSectionSchema,
		employment_questions: QuestionBlockSchema,
		documents: z.object({
			enabled: z.boolean(),
			items: z.array(DocumentItemSchema),
		}),
		terms: z.object({
			enabled: z.boolean(),
			fileName: z.string().nullable().optional(),
			blobUrl: z.string().nullable().optional(),
		}),
	}),
})

function allOff(kind: FormKind, section: string): FieldToggleMap {
	const fields: FieldToggleMap = {}
	for (const field of catalogFields(kind, section)) {
		fields[field.key] = false
	}
	return fields
}

function allOn(kind: FormKind, section: string): FieldToggleMap {
	const fields: FieldToggleMap = {}
	for (const field of catalogFields(kind, section)) {
		fields[field.key] = true
	}
	return fields
}

function fieldDefaults(
	kind: FormKind,
	section: string,
	onKeys: readonly string[],
): FieldToggleMap {
	const on = new Set(onKeys)
	const fields: FieldToggleMap = {}
	for (const field of catalogFields(kind, section)) {
		fields[field.key] = on.has(field.key)
	}
	return fields
}

function prebuiltAll(
	kind: FormKind,
	section: 'rental_questions' | 'employment_questions',
	enabled: boolean,
): Record<string, boolean> {
	const map: Record<string, boolean> = {}
	for (const question of catalogPrebuiltQuestions(kind, section)) {
		map[question.key] = enabled
	}
	return map
}

export function defaultFormConfig(kind: FormKind): FormConfigV1 {
	if (kind === 'student') {
		return {
			version: 1,
			sections: {
				occupant: { enabled: true, fields: allOn('student', 'occupant') },
				pet: {
					enabled: true,
					fields: fieldDefaults('student', 'pet', ['amount', 'type', 'weight']),
				},
				vehicle: {
					enabled: true,
					fields: fieldDefaults('student', 'vehicle', ['amount', 'type']),
				},
				landlord_contact: {
					enabled: true,
					fields: allOn('student', 'landlord_contact'),
				},
				rental_questions: {
					enabled: true,
					prebuilt: prebuiltAll('student', 'rental_questions', true),
					custom: [],
				},
				employer_contact: {
					enabled: true,
					fields: allOn('student', 'employer_contact'),
				},
				employment_questions: {
					enabled: true,
					prebuilt: prebuiltAll('student', 'employment_questions', true),
					custom: [],
				},
				documents: {
					enabled: false,
					items: SUGGESTED_STUDENT_DOCS.map((doc) => ({
						id: doc.id,
						name: doc.name,
						mandatory: false,
					})),
				},
				terms: { enabled: false },
			},
		}
	}

	return {
		version: 1,
		guarantor: { ask: 'every_applicant', timing: 'requested' },
		sections: {
			next_of_kin: {
				enabled: false,
				fields: allOff('standard', 'next_of_kin'),
			},
			pet: {
				enabled: true,
				fields: fieldDefaults('standard', 'pet', ['amount', 'type', 'weight']),
			},
			vehicle: {
				enabled: true,
				fields: fieldDefaults('standard', 'vehicle', ['amount', 'type']),
			},
			min_living_history: { enabled: false, years: 1 },
			landlord_contact: {
				enabled: true,
				fields: allOn('standard', 'landlord_contact'),
			},
			rental_questions: {
				enabled: false,
				prebuilt: prebuiltAll('standard', 'rental_questions', false),
				custom: [],
			},
			employer_contact: {
				enabled: true,
				fields: allOn('standard', 'employer_contact'),
			},
			employment_questions: {
				enabled: false,
				prebuilt: prebuiltAll('standard', 'employment_questions', false),
				custom: [],
			},
			documents: {
				enabled: false,
				items: SUGGESTED_STANDARD_DOCS.map((doc) => ({
					id: doc.id,
					name: doc.name,
					mandatory: false,
				})),
			},
			terms: { enabled: false },
		},
	}
}

/**
 * Custom templates start with sections off. Pet/Vehicle keep Count + Type
 * pre-selected (floors) so turning the section on matches system defaults.
 * Student catalog questions are included on so they can be switched off.
 * Upload Documents lists the same suggested rows as the system default
 * template (section still off) so landlords can enable / edit / delete them.
 */
export function blankFormConfig(kind: FormKind): FormConfigV1 {
	if (kind === 'student') {
		return {
			version: 1,
			sections: {
				occupant: { enabled: false, fields: allOff('student', 'occupant') },
				pet: {
					enabled: false,
					fields: fieldDefaults('student', 'pet', ['amount', 'type']),
				},
				vehicle: {
					enabled: false,
					fields: fieldDefaults('student', 'vehicle', ['amount', 'type']),
				},
				landlord_contact: {
					enabled: false,
					fields: allOff('student', 'landlord_contact'),
				},
				rental_questions: {
					enabled: true,
					prebuilt: prebuiltAll('student', 'rental_questions', true),
					custom: [],
				},
				employer_contact: {
					enabled: false,
					fields: allOff('student', 'employer_contact'),
				},
				employment_questions: {
					enabled: true,
					prebuilt: prebuiltAll('student', 'employment_questions', true),
					custom: [],
				},
				documents: {
					enabled: false,
					items: SUGGESTED_STUDENT_DOCS.map((doc) => ({
						id: doc.id,
						name: doc.name,
						mandatory: false,
					})),
				},
				terms: { enabled: false },
			},
		}
	}

	return {
		version: 1,
		guarantor: { ask: 'every_applicant', timing: 'requested' },
		sections: {
			next_of_kin: {
				enabled: false,
				fields: allOff('standard', 'next_of_kin'),
			},
			pet: {
				enabled: false,
				fields: fieldDefaults('standard', 'pet', ['amount', 'type']),
			},
			vehicle: {
				enabled: false,
				fields: fieldDefaults('standard', 'vehicle', ['amount', 'type']),
			},
			min_living_history: { enabled: false, years: 1 },
			landlord_contact: {
				enabled: false,
				fields: allOff('standard', 'landlord_contact'),
			},
			rental_questions: {
				enabled: false,
				prebuilt: prebuiltAll('standard', 'rental_questions', false),
				custom: [],
			},
			employer_contact: {
				enabled: false,
				fields: allOff('standard', 'employer_contact'),
			},
			employment_questions: {
				enabled: false,
				prebuilt: prebuiltAll('standard', 'employment_questions', false),
				custom: [],
			},
			documents: {
				enabled: false,
				items: SUGGESTED_STANDARD_DOCS.map((doc) => ({
					id: doc.id,
					name: doc.name,
					mandatory: false,
				})),
			},
			terms: { enabled: false },
		},
	}
}

function defaultFieldsFor(kind: FormKind, section: string): FieldToggleMap {
	const config = defaultFormConfig(kind)
	const sectionMap: Record<string, ToggleSection | undefined> = {
		next_of_kin: config.sections.next_of_kin,
		occupant: config.sections.occupant,
		pet: config.sections.pet,
		vehicle: config.sections.vehicle,
		landlord_contact: config.sections.landlord_contact,
		employer_contact: config.sections.employer_contact,
	}
	return sectionMap[section]?.fields ?? allOn(kind, section)
}

function mergeFields(
	kind: FormKind,
	section: string,
	stored: FieldToggleMap | undefined,
): FieldToggleMap {
	const defaults = defaultFieldsFor(kind, section)
	if (!stored) return defaults
	const next: FieldToggleMap = { ...defaults }
	for (const key of Object.keys(defaults)) {
		if (typeof stored[key] === 'boolean') next[key] = stored[key]
	}
	// Legacy vehicle Make + Model chips → single Make & Model field.
	if (
		section === 'vehicle' &&
		typeof stored.make_model !== 'boolean' &&
		(stored.make === true || stored.model === true)
	) {
		next.make_model = true
	}
	return next
}

function mergeQuestionBlock(
	kind: FormKind,
	section: 'rental_questions' | 'employment_questions',
	stored: QuestionBlock | undefined,
	fallbackEnabled: boolean,
): QuestionBlock {
	const catalog = catalogPrebuiltQuestions(kind, section)
	const prebuilt: Record<string, boolean> = {}
	for (const question of catalog) {
		prebuilt[question.key] =
			typeof stored?.prebuilt?.[question.key] === 'boolean'
				? stored.prebuilt[question.key]
				: fallbackEnabled
	}
	const custom = (stored?.custom ?? []).filter(
		(question) => CustomQuestionSchema.safeParse(question).success,
	)
	return {
		enabled: stored?.enabled ?? fallbackEnabled,
		prebuilt,
		custom,
	}
}

function enforceFloors(
	kind: FormKind,
	section: string,
	toggle: ToggleSection,
): ToggleSection {
	if (!toggle.enabled) return toggle
	const floors = floorKeys(kind, section)
	if (floors.length === 0) return toggle
	const fields = { ...toggle.fields }
	for (const key of floors) {
		fields[key] = true
	}
	return { ...toggle, fields }
}

/** Next of kin: section on → every field on; section off → every field off. */
function enforceAllFieldsWhenEnabled(
	kind: FormKind,
	section: string,
	toggle: ToggleSection,
): ToggleSection {
	return {
		...toggle,
		fields: toggle.enabled ? allOn(kind, section) : allOff(kind, section),
	}
}

function normalizeMinLivingYears(years: number | null | undefined): number {
	return clampMinLivingHistoryYears(years)
}

function asObject(value: unknown): Record<string, unknown> {
	if (value && typeof value === 'object' && !Array.isArray(value)) {
		return value as Record<string, unknown>
	}
	return {}
}

function asToggle(value: unknown): ToggleSection | undefined {
	const obj = asObject(value)
	if (typeof obj.enabled !== 'boolean' && !obj.fields) return undefined
	const fields = asObject(obj.fields)
	const mapped: FieldToggleMap = {}
	for (const [key, flag] of Object.entries(fields)) {
		if (typeof flag === 'boolean') mapped[key] = flag
	}
	return {
		enabled: typeof obj.enabled === 'boolean' ? obj.enabled : true,
		fields: mapped,
	}
}

function asQuestionBlock(value: unknown): QuestionBlock | undefined {
	const obj = asObject(value)
	if (!obj || (obj.enabled === undefined && !obj.prebuilt && !obj.custom)) {
		return undefined
	}
	const prebuiltRaw = asObject(obj.prebuilt)
	const prebuilt: Record<string, boolean> = {}
	for (const [key, flag] of Object.entries(prebuiltRaw)) {
		if (typeof flag === 'boolean') prebuilt[key] = flag
	}
	return {
		enabled: typeof obj.enabled === 'boolean' ? obj.enabled : true,
		prebuilt,
		custom: Array.isArray(obj.custom) ? (obj.custom as CustomQuestion[]) : [],
	}
}

function asDocuments(
	value: unknown,
): FormConfigV1['sections']['documents'] | undefined {
	const obj = asObject(value)
	if (obj.enabled === undefined && !obj.items) return undefined
	return {
		enabled: typeof obj.enabled === 'boolean' ? obj.enabled : false,
		items: Array.isArray(obj.items) ? (obj.items as DocumentItem[]) : [],
	}
}

/** Fill missing catalog keys from defaults; force floors when a section is on. */
export function resolveConfig(kind: FormKind, stored: unknown): FormConfigV1 {
	const defaults = defaultFormConfig(kind)
	const raw = asObject(stored)
	const guarantorRaw = asObject(raw.guarantor)
	const sectionsRaw = asObject(raw.sections)
	const sections: Partial<FormConfigV1['sections']> = {
		next_of_kin: asToggle(sectionsRaw.next_of_kin),
		occupant: asToggle(sectionsRaw.occupant),
		pet: asToggle(sectionsRaw.pet),
		vehicle: asToggle(sectionsRaw.vehicle),
		landlord_contact: asToggle(sectionsRaw.landlord_contact),
		employer_contact: asToggle(sectionsRaw.employer_contact),
		rental_questions: asQuestionBlock(sectionsRaw.rental_questions),
		employment_questions: asQuestionBlock(sectionsRaw.employment_questions),
		documents: asDocuments(sectionsRaw.documents),
		terms: (() => {
			const terms = asObject(sectionsRaw.terms)
			if (typeof terms.enabled !== 'boolean') return undefined
			return {
				enabled: terms.enabled,
				fileName:
					typeof terms.fileName === 'string' || terms.fileName === null
						? (terms.fileName as string | null)
						: undefined,
				blobUrl:
					typeof terms.blobUrl === 'string' || terms.blobUrl === null
						? (terms.blobUrl as string | null)
						: undefined,
			}
		})(),
		min_living_history: (() => {
			const min = asObject(sectionsRaw.min_living_history)
			if (min.enabled === undefined && min.years === undefined) return undefined
			return {
				enabled: typeof min.enabled === 'boolean' ? min.enabled : false,
				years: normalizeMinLivingYears(
					typeof min.years === 'number' ? min.years : null,
				),
			}
		})(),
	}

	if (kind === 'student') {
		return {
			version: 1,
			sections: {
				occupant: enforceFloors(kind, 'occupant', {
					enabled: sections.occupant?.enabled ?? true,
					fields: mergeFields(kind, 'occupant', sections.occupant?.fields),
				}),
				pet: enforceFloors(kind, 'pet', {
					enabled: sections.pet?.enabled ?? true,
					fields: mergeFields(kind, 'pet', sections.pet?.fields),
				}),
				vehicle: enforceFloors(kind, 'vehicle', {
					enabled: sections.vehicle?.enabled ?? true,
					fields: mergeFields(kind, 'vehicle', sections.vehicle?.fields),
				}),
				landlord_contact: {
					enabled: sections.landlord_contact?.enabled ?? true,
					fields: mergeFields(
						kind,
						'landlord_contact',
						sections.landlord_contact?.fields,
					),
				},
				rental_questions: mergeQuestionBlock(
					kind,
					'rental_questions',
					sections.rental_questions,
					true,
				),
				employer_contact: {
					enabled: sections.employer_contact?.enabled ?? true,
					fields: mergeFields(
						kind,
						'employer_contact',
						sections.employer_contact?.fields,
					),
				},
				employment_questions: mergeQuestionBlock(
					kind,
					'employment_questions',
					sections.employment_questions,
					true,
				),
				documents: {
					enabled: sections.documents?.enabled ?? false,
					items: sections.documents?.items ?? defaults.sections.documents.items,
				},
				terms: {
					enabled: sections.terms?.enabled ?? false,
					fileName: sections.terms?.fileName ?? null,
					blobUrl: sections.terms?.blobUrl ?? null,
				},
			},
		}
	}

	return {
		version: 1,
		guarantor: {
			ask: 'every_applicant',
			timing: guarantorRaw.timing === 'immediate' ? 'immediate' : 'requested',
		},
		sections: {
			next_of_kin: enforceAllFieldsWhenEnabled(kind, 'next_of_kin', {
				enabled: sections.next_of_kin?.enabled ?? false,
				fields: mergeFields(kind, 'next_of_kin', sections.next_of_kin?.fields),
			}),
			pet: enforceFloors(kind, 'pet', {
				enabled: sections.pet?.enabled ?? true,
				fields: mergeFields(kind, 'pet', sections.pet?.fields),
			}),
			vehicle: enforceFloors(kind, 'vehicle', {
				enabled: sections.vehicle?.enabled ?? true,
				fields: mergeFields(kind, 'vehicle', sections.vehicle?.fields),
			}),
			min_living_history: {
				enabled: sections.min_living_history?.enabled ?? false,
				years: normalizeMinLivingYears(sections.min_living_history?.years),
			},
			landlord_contact: {
				enabled: sections.landlord_contact?.enabled ?? true,
				fields: mergeFields(
					kind,
					'landlord_contact',
					sections.landlord_contact?.fields,
				),
			},
			rental_questions: mergeQuestionBlock(
				kind,
				'rental_questions',
				sections.rental_questions,
				false,
			),
			employer_contact: {
				enabled: sections.employer_contact?.enabled ?? true,
				fields: mergeFields(
					kind,
					'employer_contact',
					sections.employer_contact?.fields,
				),
			},
			employment_questions: mergeQuestionBlock(
				kind,
				'employment_questions',
				sections.employment_questions,
				false,
			),
			documents: {
				enabled: sections.documents?.enabled ?? false,
				items: sections.documents?.items ?? defaults.sections.documents.items,
			},
			terms: {
				enabled: sections.terms?.enabled ?? false,
				fileName: sections.terms?.fileName ?? null,
				blobUrl: sections.terms?.blobUrl ?? null,
			},
		},
	}
}

export function parseAndResolveConfig(
	kind: FormKind,
	stored: unknown,
): FormConfigV1 {
	const resolved = resolveConfig(kind, stored)
	return FormConfigV1Schema.parse(resolved)
}

export function sectionEnabled(
	config: FormConfigV1,
	key: keyof FormConfigV1['sections'],
): boolean {
	const section = config.sections[key]
	if (!section) return false
	if ('enabled' in section) return Boolean(section.enabled)
	return false
}

export function fieldEnabled(
	config: FormConfigV1,
	sectionKey:
		| 'next_of_kin'
		| 'occupant'
		| 'pet'
		| 'vehicle'
		| 'landlord_contact'
		| 'employer_contact',
	fieldKey: string,
): boolean {
	const section = config.sections[sectionKey]
	if (!section || !section.enabled) return false
	return section.fields[fieldKey] === true
}

export function prebuiltQuestionEnabled(
	config: FormConfigV1,
	block: 'rental_questions' | 'employment_questions',
	key: string,
): boolean {
	const section = config.sections[block]
	if (!section.enabled) return false
	return section.prebuilt[key] === true
}

/**
 * Order-independent serialization of a form config, for comparing two configs
 * for equality. Postgres `jsonb` normalizes key order (shortest key first,
 * then bytewise), so a config read back from the database never stringifies
 * to the same bytes as the equivalent object rebuilt by
 * `parseAndResolveConfig` — comparing with a plain JSON.stringify reports
 * drift between configs that are in fact identical.
 */
export function canonicalConfigJson(config: unknown): string {
	const canonical = (value: unknown): unknown => {
		if (Array.isArray(value)) return value.map(canonical)
		if (value && typeof value === 'object') {
			return Object.fromEntries(
				Object.entries(value as Record<string, unknown>)
					.sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
					.map(([key, entry]) => [key, canonical(entry)]),
			)
		}
		return value
	}
	return JSON.stringify(canonical(config))
}

export function legacyFallbackConfig(kind: FormKind): FormConfigV1 {
	return defaultFormConfig(kind)
}
